import ComponentTitle from "../ComponentTitle";
import TrackingLine from "./TrackingLine";
import EducationValue from "./EducationValue";
import {useEffect, useState} from "react";
import Aos from "aos";
import {LandingPageService} from "../../../pages/Services/LandingPageService";

const Education = ({educationList}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [visibleItems, setVisibleItems] = useState(new Set());
    
    useEffect(() => {
        Aos.init({
            duration: 1000,
            once: true
        })
    })

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setVisibleItems(prev => new Set(prev).add(entry.target.dataset.index));
                    }
                });
            },
            { threshold: 0.3 }
        );

        const educationItems = document.querySelectorAll('[data-education-item]');
        educationItems.forEach(item => observer.observe(item));

        return () => observer.disconnect();
    }, [educationList]);
    
    return(
        <section 
            data-aos="fade-up" 
            id={"education"} 
            className="py-20 bg-slate-50 dark:bg-slate-900 transition-colors duration-300 relative overflow-hidden"
        >
            {/* Subtle separator line top */}
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 dark:via-slate-700 to-transparent"></div>
            
            <div className="container mx-auto px-6 lg:px-8 relative z-10">
                <div data-aos="fade-down" data-aos-delay="100">
                    <ComponentTitle title={'Education'} subTitle={'My academic and learning journey'} />
                </div>
                
                <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                    {/* Formal Education */}
                    <div className="space-y-2" data-aos="fade-right" data-aos-delay="200">
                        <div className="flex items-center gap-3 mb-8 group">
                            <div className="w-2 h-8 bg-slate-900 dark:bg-slate-400 rounded-full transition-transform duration-300"></div>
                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white transition-all duration-300">
                                Formal Education
                            </h3>
                            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700 ml-4"></div>
                        </div>
                        
                        <div className="relative">
                            {/* Simple timeline line */}
                            <div className="absolute left-6 top-0 bottom-0 w-px bg-slate-300 dark:bg-slate-700"></div>
                            
                            <div className="space-y-8">
                                {educationList?.formals.map((data, index) => (
                                    <div 
                                        key={index} 
                                        className="relative flex gap-6"
                                        data-education-item
                                        data-index={`formal-${index}`}
                                        data-aos="fade-left"
                                        data-aos-delay={300 + (index * 150)}
                                    >
                                        <TrackingLine 
                                            type="formal" 
                                            index={index}
                                            isVisible={visibleItems.has(`formal-${index}`)}
                                        />
                                        <EducationValue 
                                            education={data} 
                                            type="formal"
                                            index={index}
                                            isVisible={visibleItems.has(`formal-${index}`)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    
                    {/* Non-Formal Education */}
                    <div className="space-y-2" data-aos="fade-left" data-aos-delay="250">
                        <div className="flex items-center gap-3 mb-8 group">
                            <div className="w-2 h-8 bg-slate-900 dark:bg-slate-400 rounded-full transition-transform duration-300"></div>
                            <h3 className="text-2xl font-bold text-slate-900 dark:text-white transition-all duration-300">
                                Non-Formal Education
                            </h3>
                            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700 ml-4"></div>
                        </div>
                        
                        <div className="relative">
                            {/* Simple timeline line */}
                            <div className="absolute left-6 top-0 bottom-0 w-px bg-slate-300 dark:bg-slate-700"></div>
                            
                            <div className="space-y-8">
                                {educationList?.nonFormals.map((data, index) => (
                                    <div 
                                        key={data.id} 
                                        className="relative flex gap-6"
                                        data-education-item
                                        data-index={`nonformal-${index}`}
                                        data-aos="fade-right"
                                        data-aos-delay={350 + (index * 150)}
                                    >
                                        <TrackingLine 
                                            type="nonformal" 
                                            index={index}
                                            isVisible={visibleItems.has(`nonformal-${index}`)}
                                        />
                                        <EducationValue 
                                            education={data} 
                                            type="nonformal"
                                            index={index}
                                            isVisible={visibleItems.has(`nonformal-${index}`)}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
export default Education;
